import React from "react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/Button";
import { Intro } from "@/components/Intro";
import { Typography } from "@/components/Typography";

import {
  isSubscriptionActive,
  isSubscriptionCancelled,
  isUserSubscribed,
  userProfileApiToDataTransformer,
} from "@/services/UserService";
import { getAuthCookie, setCookie, isUserLoggedIn } from "@/services/CookiesService/CookiesService";

import { LayoutContext } from "@/contexts/LayoutContext";
import { ModalContext } from "@/contexts/ModalContext";

import { useApi } from "@/hooks/useApi";
import { useToast } from "@/hooks/useToast";

import { getAuthModal } from "@/modals/AuthModal";
import { paths } from "@/utils/paths";

import { patchSubscriptionPayload } from "@/interface/api/patchSubscription/types";
import postSubscriptionHandler from "@/interface/api/postSubscription";
import patchSubscriptionHandler from "@/interface/api/patchSubscription";

import getProfileHandler from "@api/getProfile";

import { GetProfileResponse } from "@/interface/api/getProfile/types";
import { SubscriptionStatus } from "@/types/subscription";
import { onFormSuccess } from "@/components/Form/types";
import { CANCELLED_SUBSCRIPTION, IPlansManager } from "./types";

export const PlansManager: React.FC<IPlansManager> = ({ data, currentPlan, userContext }) => {
  const { toast } = useToast();
  const router = useRouter();

  const [isLoading, setIsLoading] = React.useState(false);

  const modalContext = React.useContext(ModalContext);
  const layoutContext = React.useContext(LayoutContext);

  const { request: requestProfile } = useApi<GetProfileResponse>(getProfileHandler.getProfile);
  const { request: requestPatchSubscription } = useApi(patchSubscriptionHandler.patchSubscription);
  const { request: requestPostSubscription } = useApi(postSubscriptionHandler.postSubscription);

  const isStatusActive = isUserSubscribed(userContext) && isSubscriptionActive(userContext);

  const onContinueToPayHandler = () => {
    if (!isUserLoggedIn()) {
      return showAuthModal();
    }

    onChangePlan();
  };

  const onAuthSuccess = (type: "login" | "register") => {
    const data =
      type === "login"
        ? layoutContext?.modalData?.loginData
        : layoutContext?.modalData?.registerData;
    return {
      onFormSuccess: () => {
        onChangePlan();
        modalContext.handleModal();
      },
      data,
    };
  };

  const showAuthModal = () => {
    modalContext?.handleModal({
      content: getAuthModal({
        formType: "register",
        onLogin: onAuthSuccess("login") as {
          onFormSuccess: onFormSuccess;
        },
        onRegister: onAuthSuccess("register") as {
          onFormSuccess: onFormSuccess;
        },
        onRecover: {
          onFormSuccess: () => {
            modalContext.handleModal();
          },
        },
        modalData: layoutContext?.modalData,
      }),
      modalProps: {
        className: "u-modal modal__auth u-box u-box u-box--small u-text-center",
      },
      onCancelCallback: () => {
        modalContext.handleModal();
        setIsLoading(false);
      },
    });
  };

  const getProfile = async () => {
    const user = await getUserProfileData();
    if (user) {
      user.subscription?.status === SubscriptionStatus.ACTIVE
        ? router.push("/my-qr-codes")
        : assignSubscription(user.subscription.id);
    }
  };

  const assignSubscription = (subscriptionId?: string) => {
    if (!currentPlan) return;
    const authorization = getAuthCookie();

    const requestData: patchSubscriptionPayload = {
      userSubscriptionPlanId: subscriptionId || userContext.user.subscription.id,
      subscriptionId: currentPlan.id,
      authorization,
    };

    if (!requestData.userSubscriptionPlanId) {
      setIsLoading(true);
      getProfile();
    } else {
      requestPatchSubscription(requestData)
        .then((res) => {
          if (res.status === 204 || res.status === 200) {
            userContext.setUser({
              ...userContext.user,
              subscription: {
                ...userContext.user.subscription,
                plan: currentPlan.data,
              },
            });
            router.push(paths.payment());
            setIsLoading(false);
          }
        })
        .catch((error) => {
          toast({ message: "genericError", type: "error" });
          setIsLoading(false);
        });
    }
  };

  const renewSubscription = () => {
    requestPostSubscription()
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          // TODO: This profile request is not necessary. Ask to BE for return the new created user subscription id in the resquestPostSubscription response
          getProfile();
        }
      })
      .catch((error) => {
        toast({ message: "genericError", type: "error" });
        setIsLoading(false);
      });
  };

  const getUserProfileData = async () => {
    try {
      const res = await requestProfile({ authorization: getAuthCookie() });
      const user = userProfileApiToDataTransformer(res?.data);

      return user;
    } catch (error) {
      toast({ message: "genericError", type: "error" });
      setIsLoading(false);
    }
  };

  const onChangePlan = async () => {
    const user = await getUserProfileData();

    if (user && currentPlan) {
      setIsLoading(true);

      userContext.setUser({
        ...userContext.user,
        selectedSubscriptionId: currentPlan.id,
      });

      if (isSubscriptionCancelled(user)) {
        // Add cancelled sub status to cookie, since it is removed once we make the post to the subscription endpoint
        setCookie(CANCELLED_SUBSCRIPTION, true);
        return renewSubscription();
      }
      assignSubscription();
    }
  };

  const renderTitleContent = () => {
    if (isStatusActive) {
      return <h1 className="intro-banner__title">{data?.title}</h1>;
    }

    return (
      <div className="intro-banner__subtitle__wrapper">
        <Typography
          Tag="p"
          classname="intro-banner__subtitle">
          {!isUserLoggedIn() ? data?.subtitle_1 : data?.subtitle_2}
        </Typography>
        <div className="intro-banner__button">
          <Button
            disabled={isLoading}
            dataQA="pricing-continue-button"
            onClick={onContinueToPayHandler}
            icon={isLoading}
            loading={isLoading}
            label={isLoading ? "loading" : "Continue"}
          />
        </div>
      </div>
    );
  };

  return (
    <Intro className={`intro-banner pricing intro-banner--${data?.version}`}>
      <div className="intro-banner__wrapper">{renderTitleContent()}</div>
    </Intro>
  );
};
